import {
  Box, Button, Card, CardContent, CircularProgress, Grid, Typography
} from '@material-ui/core';
import { useAsync } from 'hooks/useAsync';
import { TransactionDetails, TransactionType, buildDelegationTransactionText, hasAmount } from 'models';
import { transactionService } from 'services/transactionService';

interface ITransactionRejectProps {
  transactionId: string;
  oneTimeCode: string;
  transactionDetails: TransactionDetails;
}

const getTitleByType = (type: TransactionType): string => {
  switch (type) {
    case TransactionType.WITHDRAW:
      return 'Reject withdrawal';
    case TransactionType.SIGN:
      return 'Reject signing of message';
    case TransactionType.DELEGATE:
      return 'Reject delegation';
    default:
      return '';
  }
};

const Row = ({ label, content }: { label: string, content: string }) => (
  <Grid container>
    <Grid item md={3}>
      <Typography variant="body2" align="right">{label}</Typography>
    </Grid>
    <Grid item md={9}>
      <Typography variant="body1" style={{ wordBreak: 'break-all' }}>{content}</Typography>
    </Grid>
  </Grid>
);

export const TransactionReject = ({ transactionId, oneTimeCode, transactionDetails }: ITransactionRejectProps) => {
  const reject = useAsync(() => transactionService.rejectTransaction(transactionId, oneTimeCode));

  const toNonScientificStr = (decimal: number): string => {
    const str = decimal.toString();
    if (str.includes('e-')) {
      const [num, decimalPlaces] = str?.split('e-');
      const leadingZeros = Array(...Array(parseInt(decimalPlaces, 10) - 1)).reduce((acc) => `${acc}0`, '');
      const nonDecimalNumber = num.toString().replace('.', '');
      return `0.${leadingZeros}${nonDecimalNumber}`;
    }
    return str;
  };

  return (
    <>
      <Box display="flex" pb={4}>
        <Typography variant="h3">
          {getTitleByType(transactionDetails.transactionType!)}
        </Typography>
      </Box>
      <Card>
        <CardContent>
          <Grid container>
            <Grid item md={12}><Typography variant="h5">Transaction details</Typography></Grid>
          </Grid>
          <Box pt={2}>
            {TransactionType.WITHDRAW === transactionDetails.transactionType
              && (
                <>
                  <Row label="From address" content={transactionDetails.fromPublicKey} />
                  <Row label="To address" content={transactionDetails.toPublicKey} />
                  <Row label="Amount" content={`${toNonScientificStr(transactionDetails.amount)} ${transactionDetails.symbol}`} />
                </>
              )}
            {TransactionType.SIGN === transactionDetails.transactionType
              && (
                <>
                  <Row label="From address" content={transactionDetails.fromPublicKey} />
                  <Row label="Message" content={transactionDetails.ref} />
                </>
              )}
            {[TransactionType.DELEGATE, TransactionType.MANAGE].includes(transactionDetails.transactionType!)
              && (
                <>
                  <Row label="Request" content={buildDelegationTransactionText(transactionDetails.transactionSubType)} />
                  <Row label="From address" content={transactionDetails.fromPublicKey} />
                  <Row label="To address" content={transactionDetails.toPublicKey} />
                  {hasAmount(transactionDetails.transactionSubType) && (
                    <Row label="Amount" content={`${toNonScientificStr(transactionDetails.amount)} ${transactionDetails.symbol}`} />
                  )}
                </>
              )}
          </Box>
          {
            reject.isSuccess
              ? <Typography variant="caption">The transaction has been successfully rejected. If you would like to execute this transaction nonetheless, a new request has to be initiated via our web-portal.</Typography>
              : (
                <Box pt={4}>
                  <Grid container>
                    <Grid item md={3} />
                    <Grid item md={9}>
                      <Button
                        variant="contained"
                        disabled={reject.isLoading || reject.isSuccess}
                        color="primary"
                        onClick={() => reject.exec()}
                        endIcon={reject.isLoading && <CircularProgress size="1rem" color="secondary" style={{ margin: 0 }} />}
                      >
                        Reject
                      </Button>
                      {
                        reject.isError && reject.error
                        && <Typography variant="caption" color="primary">{reject?.error?.data[0]?.description}</Typography>
                      }
                    </Grid>
                  </Grid>
                </Box>
              )
          }
        </CardContent>
      </Card>
    </>
  );
};
